import { doc, getDoc, setDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface FeatureFlag {
	id: string;
	name: string;
	description: string;
	category: string;
	isGloballyEnabled: boolean;
	enabledForRoles: string[];
	createdAt: string;
	createdBy: string;
	updatedAt?: string;
	updatedBy?: string;
}

export interface FeatureFlagsData {
	flags: { [key: string]: FeatureFlag };
	lastUpdated: string;
	version: number;
	_userId?: string;
}

export interface SystemSettings {
	staticData: {
		mortgageRates: any;
		dreamHome: any;
		globalSettings: any;
	};
	featureFlags?: FeatureFlagsData;
}

export class SystemSettingsService {
	private static readonly SETTINGS_DOC_ID = 'default';
	private static readonly FEATURE_FLAGS_DOC_ID = 'featureFlags';
	private static readonly COLLECTION_NAME = 'systemSettings';
	private static readonly STATIC_DATA_COLLECTION = 'staticData';

	/**
	 * Read the live settings.
	 *
	 * Throws on failure so the UI can show a real error state. It must NOT fall
	 * back to hardcoded defaults: doing so rendered the Settings page showing
	 * fabricated mortgage rates (6.63% / 5.75%) as if they were live, which an
	 * admin could then save straight over the real data. getDefaultSettings(),
	 * the source of those figures, has been deleted.
	 */
	static async getSettings(): Promise<SystemSettings> {
		try {
			// Always read from staticData collection for the most current data
			const [dreamHomeDoc, mortgageRatesDoc, staticDoc] = await Promise.all([
				getDoc(doc(db, this.STATIC_DATA_COLLECTION, 'dreamHome')),
				getDoc(doc(db, this.STATIC_DATA_COLLECTION, 'mortgageRates')),
				getDoc(doc(db, this.STATIC_DATA_COLLECTION, 'static'))
			]);

			return {
				staticData: {
					dreamHome: dreamHomeDoc.exists() ? dreamHomeDoc.data() : {},
					mortgageRates: mortgageRatesDoc.exists() ? mortgageRatesDoc.data() : {},
					globalSettings: staticDoc.exists() ? staticDoc.data() : {}
				}
			};
		} catch (error) {
			console.error('Error fetching system settings:', error);
			throw error;
		}
	}

	/**
	 * Persist settings to exactly the documents getSettings()/getFeatureFlags() read.
	 *
	 * Every write MERGES. These documents are co-owned by the backend:
	 * `staticData/mortgageRates` is written by the scheduled getMortgageRates job
	 * and `staticData/static` holds `appreciationRate`, which
	 * functions-shared/src/util/mortgageCalculations.ts reads for every mortgage
	 * appreciation calculation in the mobile app. A full-document setDoc from the
	 * portal - which never loads `appreciationRate` and cannot round-trip it -
	 * silently deleted it.
	 */
	static async updateSettings(settings: Partial<SystemSettings>): Promise<void> {
		try {
			// staticData collection documents (merge - backend-owned keys must survive)
			if (settings.staticData) {
				const writes: Promise<void>[] = [];

				if (settings.staticData.dreamHome) {
					writes.push(setDoc(
						doc(db, this.STATIC_DATA_COLLECTION, 'dreamHome'),
						settings.staticData.dreamHome,
						{ merge: true }
					));
				}
				if (settings.staticData.mortgageRates) {
					writes.push(setDoc(
						doc(db, this.STATIC_DATA_COLLECTION, 'mortgageRates'),
						settings.staticData.mortgageRates,
						{ merge: true }
					));
				}
				if (settings.staticData.globalSettings) {
					writes.push(setDoc(
						doc(db, this.STATIC_DATA_COLLECTION, 'static'),
						settings.staticData.globalSettings,
						{ merge: true }
					));
				}

				await Promise.all(writes);
			}

			// Feature flags live in systemSettings/featureFlags - the document
			// getFeatureFlags() reads. They used to be written to
			// systemSettings/default, where nothing ever looked for them.
			if (settings.featureFlags) {
				await setDoc(
					doc(db, this.COLLECTION_NAME, this.FEATURE_FLAGS_DOC_ID),
					{ ...settings.featureFlags, lastUpdated: new Date().toISOString() },
					{ merge: true }
				);
			}

			// Everything else (company / notifications / insights / integrations)
			// belongs to systemSettings/default. The backend reads
			// `integrations.plaidEnabled` from it
			// (functions-financial/src/secureAPIs/func-plaidSecureApi.ts).
			// staticData and featureFlags are excluded so this document never
			// becomes a second, stale copy of them.
			const { staticData, featureFlags, ...otherSettings } = settings as Record<string, unknown> & Partial<SystemSettings>;
			void staticData;
			void featureFlags;

			if (Object.keys(otherSettings).length > 0) {
				await setDoc(
					doc(db, this.COLLECTION_NAME, this.SETTINGS_DOC_ID),
					{ ...otherSettings, updatedAt: Timestamp.now() },
					{ merge: true }
				);
			}
		} catch (error) {
			console.error('Error updating system settings:', error);
			throw error;
		}
	}

	static async getFeatureFlags(): Promise<FeatureFlagsData | null> {
		try {
			const featureFlagsDoc = await getDoc(doc(db, this.COLLECTION_NAME, this.FEATURE_FLAGS_DOC_ID));

			if (featureFlagsDoc.exists()) {
				return featureFlagsDoc.data() as FeatureFlagsData;
			}

			return null;
		} catch (error) {
			console.error('Error fetching feature flags:', error);
			throw error;
		}
	}

	/**
	 * Toggle/patch a single feature flag.
	 *
	 * Runs in a transaction: the read of the current flag and the write of the
	 * merged result have to be one atomic step, otherwise two admins editing the
	 * same flag concurrently both read the pre-change value and the second write
	 * silently discards the first admin's change.
	 */
	static async updateFeatureFlag(
		flagId: string,
		updates: Partial<FeatureFlag>,
		agentId: string
	): Promise<void> {
		try {
			const featureFlagsRef = doc(db, this.COLLECTION_NAME, this.FEATURE_FLAGS_DOC_ID);

			await runTransaction(db, async (transaction) => {
				const featureFlagsDoc = await transaction.get(featureFlagsRef);

				if (!featureFlagsDoc.exists()) {
					throw new Error('Feature flags document not found');
				}

				const currentData = featureFlagsDoc.data() as FeatureFlagsData;
				const currentFlag = currentData.flags?.[flagId];

				if (!currentFlag) {
					throw new Error(`Feature flag ${flagId} not found`);
				}

				// Merge the updates with current flag data
				const updatedFlag = {
					...currentFlag,
					...updates,
					updatedAt: new Date().toISOString(),
					updatedBy: agentId
				};

				// Field-path update so sibling flags are never rewritten
				transaction.update(featureFlagsRef, {
					[`flags.${flagId}`]: updatedFlag,
					lastUpdated: new Date().toISOString()
				});
			});
		} catch (error) {
			console.error('Error updating feature flag:', error);
			throw error;
		}
	}
}